// src/utils/buttonUtils.js
import { documentUploadPDFService } from 'src/api/document'
import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import SelectedIcon from 'src/components/selected-icon'
import React from 'react'
import { Typography } from '@material-ui/core'
import {
  messageBoxHideAction,
  messageBoxShowAction,
} from 'src/reducers/message-box/action'

// Constants
export const BUTTON_TYPES = {
  LINK: 'link',
  PDF: 'pdf',
}

export const DEFAULT_ICON_SIZE = 18

// Format Functions
export const formatPDFName = name => {
  if (!name) return ''
  if (name.length > 16) {
    const extension = name.split('.').pop()
    return name.substring(0, 16) + '...' + extension
  }
  return name
}

// Model Update Functions
export const createSimpleButton = (
  writer,
  {
    href,
    textColor,
    text,
    type,
    pdfName,
    selectedIcon,
    selectedIconName,
    size,
    openIn,
  },
) => {
  const simpleButton = writer.createElement('simpleButton', {
    href,
    textcolor: textColor,
    text,
    type,
    pdfname: pdfName,
    targervalue: openIn,
  })

  const textNode = writer.createText(text)

  const simpleIcon = writer.createElement('simpleIcon', {
    icon: selectedIcon,
    iconname: selectedIconName,
    width: size,
    height: size,
  })

  return {
    simpleButton,
    textNode,
    simpleIcon,
  }
}

export const handleButtonUpdate = (editor, position, buttonProps) => {
  editor.model.change(writer => {
    const existingButton = position.parent.getChild(position.offset)
    if (existingButton && existingButton.name === 'simpleButton') {
      writer.remove(existingButton)
    }
  })

  editor.model.change(writer => {
    const simpleButton = writer.createElement('simpleButton', {
      href: buttonProps.href,
      textcolor: buttonProps.textColor,
      text: buttonProps.text,
      type: buttonProps.type,
      pdfname: buttonProps.pdfName,
      targetvalue: buttonProps.targetvalue || '_blank',
    })

    const textNode = writer.createText(buttonProps.text)
    const simpleIcon = writer.createElement('simpleIcon', {
      icon: buttonProps.selectedIcon,
      iconname: buttonProps.selectedIconName,
      width: buttonProps.size,
      height: buttonProps.size,
    })

    editor.model.insertContent(simpleButton, position)
    writer.append(simpleIcon, simpleButton)
    writer.append(textNode, simpleButton)
  })
}

// Action Handlers
export const handleDestinationChange = (newDestination, setDestination) => {
  if (newDestination !== null) {
    setDestination(newDestination)
  }
}

export const handleTextChange = (
  event,
  {
    editor,
    position,
    linkValue,
    textColor,
    destination,
    pdfName,
    selectedIcon,
    selectedIconName,
    setTitle,
    openIn,
  },
) => {
  const newText = event.target.value
  setTitle(newText)
  handleButtonUpdate(editor, position, {
    href: linkValue,
    textColor,
    text: newText,
    type: destination,
    pdfName,
    selectedIcon,
    selectedIconName,
    size: DEFAULT_ICON_SIZE,
    targetvalue: openIn,
  })
}

export const handleRadioChange = (
  event,
  {
    editor,
    position,
    linkValue,
    textColor,
    destination,
    pdfName,
    selectedIcon,
    selectedIconName,
    setTitle,
    openIn,
  },
) => {
  const newText = event.target.value
  setTitle(newText)
  handleButtonUpdate(editor, position, {
    href: linkValue,
    textColor,
    text: newText,
    type: destination,
    pdfName,
    selectedIcon,
    selectedIconName,
    size: DEFAULT_ICON_SIZE,
    targetvalue: openIn,
  })
}

export const handleColorChange = (
  event,
  {
    editor,
    position,
    linkValue,
    title,
    destination,
    pdfName,
    selectedIcon,
    selectedIconName,
    setTextColor,
    openIn,
  },
) => {
  const newColor = event.hex
  setTextColor(newColor)
  handleButtonUpdate(editor, position, {
    href: linkValue,
    textColor: newColor,
    text: title,
    type: destination,
    pdfName,
    selectedIcon,
    selectedIconName,
    size: DEFAULT_ICON_SIZE,
    targetvalue: openIn,
  })
}

export const handleLinkChange = (
  event,
  {
    editor,
    position,
    textColor,
    title,
    pdfName,
    selectedIcon,
    selectedIconName,
    setLink,
    setPDF,
    openIn,
  },
) => {
  if (event !== null) {
    const newLink = event.target.value
    setLink(newLink)
    setPDF(null)

    handleButtonUpdate(editor, position, {
      href: newLink,
      textColor,
      text: title,
      type: 'link',
      pdfName,
      selectedIcon,
      selectedIconName,
      targetvalue: openIn,
      size: DEFAULT_ICON_SIZE,
    })
  }
}

// File Handlers
export const handleFileUpload = async (
  file,
  {
    editor,
    position,
    textColor,
    title,
    selectedIcon,
    selectedIconName,
    setIsLoading,
    setPDF,
    setLink,
    openIn,
  },
) => {
  setIsLoading(true)
  const formData = new FormData()
  formData.append('file', file)

  try {
    const response = await documentUploadPDFService(formData)
    const fileUrl = process.env.REACT_APP_CDN + response.data.url

    setPDF(formatPDFName(file.name))
    setLink(fileUrl)

    handleButtonUpdate(editor, position, {
      href: fileUrl,
      textColor,
      text: title,
      type: 'pdf',
      pdfName: file.name,
      selectedIcon,
      selectedIconName,
      size: DEFAULT_ICON_SIZE,
      targetvalue: openIn,
    })

    setIsLoading(false)
    return true
  } catch (error) {
    console.error('File upload failed:', error)
    setIsLoading(false)
    return false
  }
}

export const handleFileDelete = ({
  editor,
  position,
  textColor,
  title,
  destination,
  selectedIcon,
  selectedIconName,
  setPDF,
  setLink,
  openIn,
}) => {
  setPDF('')
  setLink('')

  handleButtonUpdate(editor, position, {
    href: '',
    textColor,
    text: title,
    type: destination,
    pdfName: '',
    selectedIcon,
    selectedIconName,
    size: DEFAULT_ICON_SIZE,
    targetvalue: openIn,
  })
}

// Icon Handlers
export const handleIconSelect = (
  icon,
  {
    editor,
    position,
    linkValue,
    textColor,
    title,
    destination,
    pdfName,
    setSelectedIcon,
    setSelectedIconName,
    openIn,
  },
) => {
  setSelectedIcon(icon)
  setSelectedIconName(icon)

  handleButtonUpdate(editor, position, {
    href: linkValue,
    textColor,
    text: title,
    type: destination,
    pdfName,
    selectedIcon: icon,
    selectedIconName: icon,
    size: DEFAULT_ICON_SIZE,
    targetvalue: openIn,
  })
}

export const handleIconDelete = ({
  editor,
  position,
  linkValue,
  textColor,
  title,
  destination,
  pdfName,
  setSelectedIcon,
  setSelectedIconName,
  openIn,
}) => {
  setSelectedIcon('')
  setSelectedIconName('lets-icons:add-round')

  handleButtonUpdate(editor, position, {
    href: linkValue,
    textColor,
    text: title,
    type: destination,
    pdfName,
    selectedIcon: '',
    selectedIconName: 'lets-icons:add-round',
    size: DEFAULT_ICON_SIZE,
    targetvalue: openIn,
  })
}

export const showIconSelector = ({
  editor,
  target,
  handleIconSelect,
  dispatch,
}) => {
  const handleClickOutside = event => {
    const messageBox = document.getElementById('message-box-1')
    if (messageBox && !messageBox.contains(event.target)) {
      document.removeEventListener('mousedown', handleClickOutside)
      dispatch(messageBoxHideAction('message-box-1'))
    }
  }

  document.addEventListener('mousedown', handleClickOutside)

  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        title: `<div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
            <Typography>Select Icon</Typography>
          </div>`,
        buttons: [],
        component: (
          <SelectedIcon
            editor={editor}
            target={target}
            onIconSelect={handleIconSelect}
          />
        ),
        variant: MessageBoxVariant.DRAGGABLE,
        callback: () => {},
      }),
    ),
  )
}

// Other Utility Functions
export const deleteButton = (editor, position) => {
  editor.model.change(writer => {
    const existingButton = position.parent.getChild(position.offset)
    if (existingButton && existingButton.name === 'simpleButton') {
      writer.remove(existingButton)
    }
  })
}

export const handleTargetChange = (
  event,
  {
    editor,
    position,
    linkValue,
    textColor,
    title,
    destination,
    pdfName,
    selectedIcon,
    selectedIconName,
    setOpenIn,
    openIn,
  },
) => {
  const newTarget = event
  setOpenIn(newTarget)

  handleButtonUpdate(editor, position, {
    href: linkValue,
    textColor,
    text: title,
    type: destination,
    pdfName,
    selectedIcon,
    selectedIconName,
    size: DEFAULT_ICON_SIZE,
    targetvalue: newTarget, // Add target property
  })
}

export const getInitialButtonState = selection => {
  if (selection && selection._children && selection._children._nodes) {
    const icon = selection._children._nodes.filter(
      item => item.name === 'simpleIcon',
    )

    return {
      selectedIcon: icon[0]?.getAttribute('icon') || '',
      selectedIconName:
        icon[0]?.getAttribute('iconname') || 'lets-icons:add-round',
      link: selection.getAttribute('href') || '',
      textColor: selection.getAttribute('textcolor') || '',
      title: selection.getAttribute('text') || '',
      pdfName: formatPDFName(selection.getAttribute('pdfname')) || '',
      destination: selection.getAttribute('type') || BUTTON_TYPES.LINK,
      targetvalue: selection.getAttribute('targetvalue'),
    }
  }

  return {
    selectedIcon: '',
    selectedIconName: 'lets-icons:add-round',
    link: '',
    textColor: '',
    title: '',
    pdfName: '',
    destination: BUTTON_TYPES.LINK,
  }
}

export const getModelElementLink = (editor, target) => {
  if (editor && target) {
    const modelElement = editor.editing.mapper.toModelElement(target)
    if (modelElement) {
      return modelElement.getAttribute('href')
    }
  }
  return ''
}
